import { permissions as route42Permissions } from '../../../auth/permissions';
import type { BillingDto, BillingStatus } from '../../billing/types/billing';
import type { ConversationDto } from '../../conversations/types/conversation';
import type { MachineDocument } from '../../documents/types/document';
import type { InvestigationDetailResponseDto, InvestigationDto, InvestigationStatusDto } from '../../investigations/types/investigation';
import type { MachineWorkspaceResponseDto } from '../../machine-memory/types/machine-memory';
import { asCount } from '../../machine-memory/types/machine-memory';

export const APPLICATION_GUIDANCE_CONTEXT_VERSION = '1.0' as const;
export const APPLICATION_GUIDANCE_COUNT_CAP = 10_000;

export const GUIDANCE_ROUTE_CONTEXTS = [
  { id: 'customer-selection', pattern: '/organizations' },
  { id: 'machine-start', pattern: '/start' },
  { id: 'machines', pattern: '/machines' },
  { id: 'create-machine', pattern: '/machines/new' },
  { id: 'new-conversation', pattern: '/machines/:machineId/conversations/new' },
  { id: 'conversation', pattern: '/machines/:machineId/conversations/:conversationId' },
  { id: 'conversations', pattern: '/machines/:machineId/conversations' },
  { id: 'investigation', pattern: '/machines/:machineId/investigations/:investigationId' },
  { id: 'investigations', pattern: '/machines/:machineId/investigations' },
  { id: 'add-documents', pattern: '/machines/:machineId/documents/add' },
  { id: 'document', pattern: '/machines/:machineId/documents/:documentId' },
  { id: 'documents', pattern: '/machines/:machineId/documents' },
  { id: 'machine-memory', pattern: '/machines/:machineId/memory' },
  { id: 'activity', pattern: '/machines/:machineId/activity' },
  { id: 'machine-entry', pattern: '/machines/:machineId' },
  { id: 'users-access', pattern: '/settings/users' },
  { id: 'profile', pattern: '/settings/profile' },
  { id: 'organization', pattern: '/settings/organization' },
  { id: 'billing', pattern: '/settings/billing' },
] as const;

export type GuidanceRouteId = (typeof GUIDANCE_ROUTE_CONTEXTS)[number]['id'];
export type GuidanceRoutePattern = (typeof GUIDANCE_ROUTE_CONTEXTS)[number]['pattern'];

export const GUIDANCE_ROLE_IDS = [
  'customer_admin',
  'maintenance_manager',
  'technician',
  'viewer',
] as const;
export type GuidanceRoleId = (typeof GUIDANCE_ROLE_IDS)[number];

export const GUIDANCE_PERMISSION_IDS = [
  route42Permissions.organizationRead,
  route42Permissions.facilitiesRead,
  route42Permissions.facilitiesCreate,
  route42Permissions.machineRead,
  route42Permissions.machineCreate,
  route42Permissions.documentsRead,
  route42Permissions.documentsUpload,
  route42Permissions.documentsReview,
  route42Permissions.conversationRead,
  route42Permissions.conversationCreate,
  route42Permissions.questionAsk,
  route42Permissions.investigationRead,
  route42Permissions.investigationCreate,
  route42Permissions.investigationUpdate,
  route42Permissions.investigationContribute,
  route42Permissions.investigationManageParticipants,
  route42Permissions.usersRead,
  route42Permissions.usersInvite,
  route42Permissions.usersAssignRole,
  route42Permissions.usersSuspend,
  route42Permissions.usersReactivate,
  route42Permissions.usersRemove,
  route42Permissions.billingRead,
  route42Permissions.billingManage,
] as const;
export type GuidancePermissionId = (typeof GUIDANCE_PERMISSION_IDS)[number];

export const GUIDANCE_ACTION_REQUIREMENTS = {
  'organization.choose': [],
  'machine.create': [route42Permissions.machineCreate],
  'machine.switch': [route42Permissions.machineRead],
  'document.view': [route42Permissions.documentsRead],
  'document.upload': [route42Permissions.documentsUpload],
  'document.review': [route42Permissions.documentsRead, route42Permissions.documentsReview],
  'conversation.view': [route42Permissions.conversationRead],
  'question.ask': [route42Permissions.conversationRead, route42Permissions.conversationCreate, route42Permissions.questionAsk],
  'investigation.view': [route42Permissions.investigationRead],
  'investigation.start': [route42Permissions.investigationCreate],
  'investigation.contribute': [route42Permissions.investigationContribute],
  'investigation.complete': [route42Permissions.investigationUpdate],
  'memory.view': [route42Permissions.machineRead],
  'activity.view': [route42Permissions.machineRead],
  'facility.manage': [route42Permissions.facilitiesRead, route42Permissions.facilitiesCreate],
  'users.view': [route42Permissions.usersRead],
  'users.invite': [route42Permissions.usersInvite],
  'users.assign_role': [route42Permissions.usersAssignRole],
  'users.suspend': [route42Permissions.usersSuspend],
  'users.reactivate': [route42Permissions.usersReactivate],
  'users.remove': [route42Permissions.usersRemove],
  'billing.view': [route42Permissions.billingRead],
  'billing.manage': [route42Permissions.billingManage],
  'profile.manage': [],
} as const satisfies Record<string, readonly GuidancePermissionId[]>;
export type GuidanceActionId = keyof typeof GUIDANCE_ACTION_REQUIREMENTS;

export interface GuidanceRouteContext {
  id: GuidanceRouteId;
  pattern: GuidanceRoutePattern;
}

export interface GuidanceScopeContext {
  organization_selected: boolean;
  machine?: { id: string; name?: string | null };
  document?: { id: string };
  conversation?: { id: string };
  investigation?: { id: string };
}

export interface GuidanceDocumentListState {
  total: number;
  ready: number;
  learning: number;
  needs_review: number;
  no_evidence: number;
  failed: number;
}

export interface GuidanceSafeState {
  machines?: { total: number };
  documents?: GuidanceDocumentListState;
  conversations?: { total: number };
  conversation?: {
    status: 'ACTIVE' | 'ARCHIVED';
    turn_count: number;
  };
  document?: {
    status: MachineDocument['uiStatus'];
    question_ready: boolean;
  };
  machine_memory?: {
    status: 'NOT_READY' | 'READY' | 'UPDATING' | 'UNAVAILABLE';
    knowledge_gaps: number;
    conflicts: number;
  };
  investigations?: { total: number; open: number };
  investigation?: {
    status: InvestigationStatusDto;
    findings: number;
    open_questions: number;
    conclusions: number;
    evidence: number;
  };
  billing?: { status: BillingStatus; configured: boolean };
}

export interface ApplicationContextHintV1 {
  schema_version: typeof APPLICATION_GUIDANCE_CONTEXT_VERSION;
  route: GuidanceRouteContext | null;
  scope: GuidanceScopeContext;
  safe_state?: GuidanceSafeState;
}

export interface ApplicationGuidanceContextV1 extends ApplicationContextHintV1 {
  context_level: 0 | 1 | 2 | 3;
  actor?: {
    role_ids: GuidanceRoleId[];
    permissions: GuidancePermissionId[];
    available_actions: GuidanceActionId[];
  };
}

export interface BuildApplicationContextHintInput {
  pathname: string;
  organizationSelected: boolean;
  machineName?: string | null;
  safeState?: GuidanceSafeState;
}

export interface AuthorizeApplicationContextInput {
  hint: ApplicationContextHintV1;
  roleIds?: Iterable<string>;
  permissions?: Iterable<string>;
  availableActions?: Iterable<string>;
}

interface MatchedGuidanceRoute extends GuidanceRouteContext {
  params: Record<string, string>;
}

const ROLE_ALLOWLIST = new Set<string>(GUIDANCE_ROLE_IDS);
const PERMISSION_ALLOWLIST = new Set<string>(GUIDANCE_PERMISSION_IDS);
const ACTION_ALLOWLIST = new Set<string>(Object.keys(GUIDANCE_ACTION_REQUIREMENTS));
const OPAQUE_ID = /^[A-Za-z0-9._:-]{1,160}$/;

export function matchGuidanceRoute(pathname: string): MatchedGuidanceRoute | null {
  const path = normalizePathname(pathname);
  for (const route of GUIDANCE_ROUTE_CONTEXTS) {
    const params = matchRoutePattern(route.pattern, path);
    if (params) return { id: route.id, pattern: route.pattern, params };
  }
  return null;
}

export function buildApplicationContextHint(input: BuildApplicationContextHintInput): ApplicationContextHintV1 {
  const matched = matchGuidanceRoute(input.pathname);
  const scope: GuidanceScopeContext = {
    organization_selected: Boolean(input.organizationSelected),
  };

  if (matched) {
    const machineId = sanitizeOpaqueId(matched.params.machineId);
    const documentId = sanitizeOpaqueId(matched.params.documentId);
    const conversationId = sanitizeOpaqueId(matched.params.conversationId);
    const investigationId = sanitizeOpaqueId(matched.params.investigationId);
    if (machineId) {
      const name = sanitizeDisplayName(input.machineName);
      scope.machine = name ? { id: machineId, name } : { id: machineId };
    }
    if (documentId) scope.document = { id: documentId };
    if (conversationId) scope.conversation = { id: conversationId };
    if (investigationId) scope.investigation = { id: investigationId };
  }

  const safeState = sanitizeSafeState(input.safeState);
  return {
    schema_version: APPLICATION_GUIDANCE_CONTEXT_VERSION,
    route: matched ? { id: matched.id, pattern: matched.pattern } : null,
    scope,
    ...(safeState ? { safe_state: safeState } : {}),
  };
}

/**
 * Produces the internal guidance context after an authenticated layer has
 * supplied its own roles/permissions. Browser-supplied actor data must never be
 * used to authorize Route42 operations or protected reads.
 */
export function authorizeApplicationContext(input: AuthorizeApplicationContextInput): ApplicationGuidanceContextV1 {
  const roleIds = filterAllowlist(input.roleIds, ROLE_ALLOWLIST) as GuidanceRoleId[];
  const permissionIds = filterAllowlist(input.permissions, PERMISSION_ALLOWLIST) as GuidancePermissionId[];
  const permissionSet = new Set<GuidancePermissionId>(permissionIds);
  const availableActions = filterAllowlist(input.availableActions, ACTION_ALLOWLIST)
    .filter((action): action is GuidanceActionId => action in GUIDANCE_ACTION_REQUIREMENTS)
    .filter((action) => GUIDANCE_ACTION_REQUIREMENTS[action].every((permission) => permissionSet.has(permission)));

  const hasActor = input.roleIds !== undefined && input.permissions !== undefined;
  const safeState = hasActor ? sanitizeSafeState(input.hint.safe_state) : undefined;
  const contextLevel: 0 | 1 | 2 | 3 = safeState
    ? 3
    : hasActor
      ? 2
      : input.hint.route
        ? 1
        : 0;

  return {
    schema_version: APPLICATION_GUIDANCE_CONTEXT_VERSION,
    context_level: contextLevel,
    route: input.hint.route,
    scope: sanitizeScope(input.hint.scope),
    ...(hasActor
      ? {
          actor: {
            role_ids: roleIds,
            permissions: permissionIds,
            available_actions: availableActions,
          },
        }
      : {}),
    ...(safeState ? { safe_state: safeState } : {}),
  };
}

export function summarizeMachineCount(total: number): NonNullable<GuidanceSafeState['machines']> {
  return { total: boundedCount(total) };
}

export function summarizeDocuments(documents: readonly MachineDocument[]): GuidanceDocumentListState {
  const summary: GuidanceDocumentListState = { total: 0, ready: 0, learning: 0, needs_review: 0, no_evidence: 0, failed: 0 };
  for (const document of documents.slice(0, APPLICATION_GUIDANCE_COUNT_CAP)) {
    summary.total += 1;
    if (document.uiStatus === 'ready') summary.ready += 1;
    else if (document.uiStatus === 'failed') summary.failed += 1;
    else if (document.uiStatus === 'needs-review') summary.needs_review += 1;
    else if (document.uiStatus === 'no-evidence') summary.no_evidence += 1;
    else summary.learning += 1;
  }
  return summary;
}

export function summarizeConversations(total: number): NonNullable<GuidanceSafeState['conversations']> {
  return { total: boundedCount(total) };
}

export function summarizeConversation(conversation: ConversationDto, turnCount?: number | null): NonNullable<GuidanceSafeState['conversation']> {
  const normalizedStatus = String(conversation.status ?? '').toUpperCase() === 'ARCHIVED' ? 'ARCHIVED' : 'ACTIVE';
  return { status: normalizedStatus, turn_count: boundedCount(turnCount ?? conversation.turn_count ?? 0) };
}

export function summarizeDocument(document: MachineDocument): NonNullable<GuidanceSafeState['document']> {
  return {
    status: document.uiStatus,
    question_ready: Boolean(document.questionReady),
  };
}

export function summarizeMachineMemory(
  workspace: MachineWorkspaceResponseDto | null | undefined,
  status: NonNullable<GuidanceSafeState['machine_memory']>['status'],
): NonNullable<GuidanceSafeState['machine_memory']> {
  return {
    status,
    knowledge_gaps: boundedCount(workspace ? asCount(workspace.counts?.documentation_gaps) : 0),
    conflicts: boundedCount(workspace ? asCount(workspace.counts?.known_conflicts) : 0),
  };
}

export function summarizeInvestigations(investigations: readonly InvestigationDto[]): NonNullable<GuidanceSafeState['investigations']> {
  return {
    total: boundedCount(investigations.length),
    open: boundedCount(investigations.filter((item) => item.status === 'DRAFT' || item.status === 'OPEN').length),
  };
}

export function summarizeInvestigation(detail: InvestigationDetailResponseDto): NonNullable<GuidanceSafeState['investigation']> {
  return {
    status: detail.investigation.status,
    findings: boundedCount(detail.investigation_memory?.findings.length ?? 0),
    open_questions: boundedCount(detail.investigation_memory?.open_questions.length ?? 0),
    conclusions: boundedCount(detail.investigation_memory?.conclusions.length ?? 0),
    evidence: boundedCount(detail.counts?.evidence ?? 0),
  };
}

export function summarizeBilling(billing: BillingDto): NonNullable<GuidanceSafeState['billing']> {
  return {
    status: billing.status,
    configured: Boolean(billing.billing_configured),
  };
}

function normalizePathname(value: string): string {
  const withoutQuery = String(value || '').split(/[?#]/, 1)[0] || '/';
  const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
}

function matchRoutePattern(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const expected = patternParts[index];
    const actual = pathParts[index];
    if (!expected || !actual) return null;
    if (expected.startsWith(':')) {
      try {
        params[expected.slice(1)] = decodeURIComponent(actual);
      } catch {
        return null;
      }
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
}

function sanitizeOpaqueId(value: string | null | undefined): string | null {
  const cleaned = String(value ?? '').trim();
  return OPAQUE_ID.test(cleaned) ? cleaned : null;
}

function sanitizeDisplayName(value: string | null | undefined): string | null {
  const withoutAsciiControls = [...String(value ?? '')]
    .map((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f) ? ' ' : character;
    })
    .join('');
  const cleaned = withoutAsciiControls
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);
  return cleaned || null;
}

function filterAllowlist(values: Iterable<string> | undefined, allowlist: ReadonlySet<string>): string[] {
  if (!values) return [];
  return [...new Set([...values].map((value) => String(value)).filter((value) => allowlist.has(value)))].sort();
}

function sanitizeScope(scope: GuidanceScopeContext): GuidanceScopeContext {
  const result: GuidanceScopeContext = { organization_selected: Boolean(scope.organization_selected) };
  const machineId = sanitizeOpaqueId(scope.machine?.id);
  if (machineId) {
    const name = sanitizeDisplayName(scope.machine?.name);
    result.machine = name ? { id: machineId, name } : { id: machineId };
  }
  const documentId = sanitizeOpaqueId(scope.document?.id);
  if (documentId) result.document = { id: documentId };
  const conversationId = sanitizeOpaqueId(scope.conversation?.id);
  if (conversationId) result.conversation = { id: conversationId };
  const investigationId = sanitizeOpaqueId(scope.investigation?.id);
  if (investigationId) result.investigation = { id: investigationId };
  return result;
}

function sanitizeSafeState(value: GuidanceSafeState | undefined): GuidanceSafeState | undefined {
  if (!value) return undefined;
  const result: GuidanceSafeState = {};
  if (value.machines) result.machines = { total: boundedCount(value.machines.total) };
  if (value.documents) {
    result.documents = {
      total: boundedCount(value.documents.total),
      ready: boundedCount(value.documents.ready),
      learning: boundedCount(value.documents.learning),
      needs_review: boundedCount(value.documents.needs_review),
      no_evidence: boundedCount(value.documents.no_evidence),
      failed: boundedCount(value.documents.failed),
    };
  }
  if (value.conversations) result.conversations = { total: boundedCount(value.conversations.total) };
  if (value.conversation && ['ACTIVE', 'ARCHIVED'].includes(value.conversation.status)) {
    result.conversation = { status: value.conversation.status, turn_count: boundedCount(value.conversation.turn_count) };
  }
  if (value.document && ['uploading', 'upload-interrupted', 'learning', 'ready', 'no-evidence', 'needs-review', 'failed'].includes(value.document.status)) {
    result.document = { status: value.document.status, question_ready: Boolean(value.document.question_ready) };
  }
  if (value.machine_memory && ['NOT_READY', 'READY', 'UPDATING', 'UNAVAILABLE'].includes(value.machine_memory.status)) {
    result.machine_memory = {
      status: value.machine_memory.status,
      knowledge_gaps: boundedCount(value.machine_memory.knowledge_gaps),
      conflicts: boundedCount(value.machine_memory.conflicts),
    };
  }
  if (value.investigations) {
    result.investigations = {
      total: boundedCount(value.investigations.total),
      open: boundedCount(value.investigations.open),
    };
  }
  if (value.investigation && ['DRAFT', 'OPEN', 'PAUSED', 'CLOSED', 'ARCHIVED'].includes(value.investigation.status)) {
    result.investigation = {
      status: value.investigation.status,
      findings: boundedCount(value.investigation.findings),
      open_questions: boundedCount(value.investigation.open_questions),
      conclusions: boundedCount(value.investigation.conclusions),
      evidence: boundedCount(value.investigation.evidence),
    };
  }
  if (value.billing && ['NOT_CONFIGURED', 'PENDING', 'TRIALING', 'ACTIVE', 'PAST_DUE', 'PAYMENT_ACTION_REQUIRED', 'CANCELED'].includes(value.billing.status)) {
    result.billing = { status: value.billing.status, configured: Boolean(value.billing.configured) };
  }
  return Object.keys(result).length ? result : undefined;
}

function boundedCount(value: number): number {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  return Math.min(Math.floor(number), APPLICATION_GUIDANCE_COUNT_CAP);
}
